import React, { forwardRef, useRef, useImperativeHandle } from 'react'
import classnames from 'classnames'
import { AlertCircleIcon } from '@icons'
import { Button } from '@platform/ui-kit'

import styles from './index.module.scss'

const BUTTON_PADDING = 28
const BUTTON_DISTANCE = 8

type LabelledActionButton = {
  label: string
  isLoading?: boolean
}

type BaseProps = {
  label: string
  id: string
  error?: string
  actionButton: LabelledActionButton
  type?: string
  hasError?: boolean
  className?: string
  inputClassName?: string
}

export type BaseWebsiteInputProps = BaseProps

export type InputProps = BaseWebsiteInputProps &
  Omit<React.InputHTMLAttributes<HTMLInputElement>, keyof BaseWebsiteInputProps>

export const WebsiteInput = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      id,
      actionButton,
      error,
      disabled = false,
      className = '',
      inputClassName = '',
      hasError,
      ...restProps
    }: InputProps,
    ref
  ) => {
    const innerRef = useRef<HTMLInputElement | null>(null)
    const internalButtonRef = useRef<HTMLButtonElement | null>(null)
    useImperativeHandle(ref, () => innerRef.current as HTMLInputElement)

    const renderStyle = () => {
      const buttonElement = internalButtonRef?.current
      const buttonWidth = buttonElement?.offsetWidth || 0

      return {
        paddingRight: buttonWidth ? buttonWidth + BUTTON_PADDING + BUTTON_DISTANCE : BUTTON_PADDING,
      }
    }
    return (
      <div
        className={classnames(
          styles['input-wrapper'],
          {
            [styles['has-error']]: !!error,
          },
          className
        )}
      >
        <label htmlFor={id} className={styles['label']}>
          {label}
        </label>
        <div className={styles['input-container']}>
          <input
            disabled={disabled}
            aria-describedby={error ? `${id}helperText` : ''}
            id={id}
            className={classnames(styles['input'], {
              [styles['input-error']]: !!error,
            })}
            placeholder={label}
            ref={innerRef as React.Ref<HTMLInputElement>}
            style={renderStyle()}
            {...restProps}
          />
          <Button
            disabled={disabled}
            className={styles['internal-button']}
            type="submit"
            isLoading={actionButton?.isLoading}
            variant="secondary"
            size="large"
            ref={internalButtonRef}
          >
            {actionButton?.label}
          </Button>
        </div>
        {!!error && (
          <p id={`${id}helperText`} className={styles['helper-text']}>
            <AlertCircleIcon className={styles['icon']} /> {error}
          </p>
        )}
        <Button
          disabled={disabled}
          className={styles['mobile-button']}
          type="submit"
          isLoading={actionButton?.isLoading}
          variant="secondary"
          size="large"
        >
          {actionButton?.label}
        </Button>
      </div>
    )
  }
)

export default WebsiteInput
